/* eslint-disable react/prop-types */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, PackageOpen } from "lucide-react";
import axios from "axios";
import getMediaUrl, { getDefaultProductImage } from "../../../components/tools/utils.jsx";

const fmt = (v) =>
    new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(Number(v || 0));

/**
 * CatalogPreview — muestra una parte del catálogo real de stock en el Home.
 *
 * Consume el endpoint público `/products/`. No inventa productos: si no hay
 * catálogo o la petición falla, lo dice y ofrece ir a la tienda igualmente.
 * Cubre carga, vacío y error.
 */
export default function CatalogPreview({ limit = 4 }) {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(false);
        axios.get("/products/", { params: { ordering: "-created_at" } })
            .then((res) => {
                if (cancelled) return;
                const list = Array.isArray(res.data) ? res.data : (res.data?.results ?? []);
                setProducts(list.slice(0, limit));
            })
            .catch(() => { if (!cancelled) setError(true); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [limit]);

    // ── Carga ────────────────────────────────────────────────────────────────
    if (loading) {
        return (
            <div
                className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 w-full max-w-3xl"
                aria-busy="true"
            >
                <span className="sr-only">Cargando productos…</span>
                {Array.from({ length: limit }).map((_, i) => (
                    <div key={i} className="glassCard rounded-xl overflow-hidden flex flex-col">
                        <div className="aspect-[4/3] skeleton" />
                        <div className="p-2 md:p-3 flex flex-col gap-2">
                            <div className="skeleton h-3 w-3/4" />
                            <div className="skeleton h-3 w-1/3" />
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    // ── Error o vacío: no inventamos catálogo, pero dejamos ir a la tienda ────
    if (error || products.length === 0) {
        return (
            <div
                role={error ? "alert" : "status"}
                className="glassCard rounded-2xl p-6 flex flex-col items-center gap-3 text-center max-w-md"
            >
                <PackageOpen className="w-8 h-8 text-primary opacity-70" aria-hidden />
                <p className="text-sm text-gray-600 dark:text-gray-300">
                    {error
                        ? "Ahora mismo no podemos mostrar el catálogo aquí."
                        : "Estamos preparando el catálogo de la tienda."}
                </p>
                <Link to="/store" className="btn btn-sm btn-outline gap-1">
                    Ir a la tienda <ArrowRight size={14} aria-hidden />
                </Link>
            </div>
        );
    }

    // ── Catálogo real ─────────────────────────────────────────────────────────
    return (
        <ul className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 w-full max-w-3xl list-none m-0 p-0">
            {products.map((product) => {
                const img = getDefaultProductImage(product);
                return (
                    <li key={product.id} className="min-w-0">
                        <Link
                            to={`/product/${product.id}`}
                            className="glassCard rounded-xl overflow-hidden flex flex-col group h-full
                                focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary
                                hover:scale-[1.02] transition-transform duration-200"
                        >
                            <div className="relative aspect-[4/3] overflow-hidden bg-base-200">
                                {img ? (
                                    <img
                                        src={getMediaUrl(img)}
                                        alt={product.name}
                                        loading="lazy"
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                    />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">
                                        Sin imagen
                                    </div>
                                )}
                            </div>
                            <div className="p-2 md:p-3 flex flex-col gap-0.5">
                                <h3 className="font-semibold text-xs md:text-sm dark:text-white text-gray-900 truncate">
                                    {product.name}
                                </h3>
                                <p className="text-xs font-bold text-primary">{fmt(product.price)}</p>
                            </div>
                        </Link>
                    </li>
                );
            })}
        </ul>
    );
}
